/**
 * The reason a proposal is being rejected — required, because the agent treats
 * a rejection as a standing instruction and a reason is what it learns from.
 *
 * A declarative <Modal> whose Reject button stays disabled until there is a
 * note. The earlier version refused an empty note by returning a rejected
 * promise from modal.confirm's onOk; antd re-throws that, so every empty click
 * surfaced as an unhandled rejection in the browser console.
 */
import { useEffect, useState } from 'react'
import { Input, Modal } from 'antd'

export interface RejectDialogProps {
  open: boolean
  onSubmit: (note: string) => Promise<void>
  onClose: () => void
}

export function RejectDialog({ open, onSubmit, onClose }: RejectDialogProps) {
  const [note, setNote] = useState('')
  const [busy, setBusy] = useState(false)

  useEffect(() => { if (open) setNote('') }, [open])

  const ready = note.trim().length > 0

  return (
    <Modal
      open={open}
      title="Why are you rejecting this?"
      okText="Reject"
      okButtonProps={{ danger: true, disabled: !ready, 'data-testid': 'cm-reject-ok' } as never}
      confirmLoading={busy}
      onCancel={onClose}
      onOk={async () => {
        if (!ready) return
        setBusy(true)
        try { await onSubmit(note.trim()); onClose() } finally { setBusy(false) }
      }}
      destroyOnHidden
    >
      <p style={{ marginTop: 0, fontSize: 13 }}>
        The agent treats a rejection as a standing instruction and will not file the same thing again without a new
        reason to.
      </p>
      <Input.TextArea
        rows={3}
        autoFocus
        data-testid="cm-reject-note"
        value={note}
        onChange={(e) => setNote(e.target.value)}
      />
    </Modal>
  )
}
